// The login, exactly as Login.dc.html, in every door: the email, "Email me a code", the 8-digit code,
// "Use a password instead" and "Paid with a different email?". Every email gets the same answer:
// the code step follows whatever the email, because request-code answers them all alike.
// After logging in, everyone lands on the Library whichever door they came through (decision 29);
// App does that when the session starts.
// A buyer back from a masterclass payment link lands on /masterclass?session_id=cs_... (MS-03). The
// id goes with the email they type, and with a new code, so the hub can confirm the payment with
// Stripe if Stripe's own event hasn't reached it yet. The page reads exactly as it does without one:
// the id is never shown, no email is ever taken from the address, and anything that isn't a checkout
// session's id is left out.
// The code request names the door (a product's, never the Library's), so the login link in the same
// email opens /sign-in in this door's look (screens/SignIn.jsx). When a login link no longer works,
// SignIn shows this door with linkFailed: its own heading and line, and "Email me a new code", until a
// new code has been sent; after that it's the door as ever. "Use a password instead" there shows the
// door's own password form, with no word about the link.
import { useEffect, useRef, useState } from 'react'
import { SITE, setTitle } from '../config/site.js'
import { EMAIL_SHAPE, requestCode, verifyCode, passwordLogIn, recordLogin } from '../lib/auth.js'
import { useQueryParam } from '../lib/router.jsx'
import { Pentagon } from '../shell/Marks.jsx'
import housePhoto from '../assets/pcfl-house.jpg'
import '../styles/login.css'

// A Stripe Checkout session's id, as request-code takes it
const CHECKOUT_SESSION = /^cs_(test|live)_[A-Za-z0-9]{10,250}$/

export default function Login({ door, linkFailed = false }) {
  const [step, setStep] = useState('email')
  const [linkNotice, setLinkNotice] = useState(linkFailed)
  const [mode, setMode] = useState('code')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [help, setHelp] = useState(false)
  const [resent, setResent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const codeRef = useRef(null)
  const sessionParam = useQueryParam('session_id')
  const afterPayment = sessionParam && CHECKOUT_SESSION.test(sessionParam) ? sessionParam : null

  // The expired link's words go with the code form only: "Use a password instead" shows the door's own
  // password form, and back on the code form they return, as no new code has been sent yet
  const showLinkFailed = linkNotice && mode === 'code'
  const title = showLinkFailed ? LINK_FAILED.title : door.name ? `Log in to ${door.name}` : 'Log in'
  useEffect(() => { setTitle('Log in') }, [])
  useEffect(() => { if (step === 'code') codeRef.current?.focus() }, [step])

  const cleanEmail = () => email.trim()

  async function sendCode(e) {
    e.preventDefault()
    if (busy) return
    setError('')
    if (!EMAIL_SHAPE.test(cleanEmail())) { setError('Type the email address you paid with.'); return }
    setBusy(true)
    try {
      await requestCode(cleanEmail(), afterPayment, door.key)
      setCode('')
      setResent(false)
      setLinkNotice(false)
      setStep('code')
    } catch {
      setError("That didn't go through. Check your connection and try again.")
    } finally {
      setBusy(false)
    }
  }

  async function logInWithPassword(e) {
    e.preventDefault()
    if (busy) return
    setError('')
    if (!EMAIL_SHAPE.test(cleanEmail()) || !password) { setError('Type your email address and password.'); return }
    setBusy(true)
    const { error: err } = await passwordLogIn(cleanEmail(), password)
    setBusy(false)
    if (err) { setError("That email and password don't match. Try again, or email yourself a code instead."); return }
    recordLogin('password')
  }

  async function logInWithCode(e) {
    e.preventDefault()
    if (busy) return
    setError('')
    if (code.length !== SITE.codeLength) { setError(`Type the ${SITE.codeLength}-digit code from the email.`); return }
    setBusy(true)
    const { error: err } = await verifyCode(cleanEmail(), code)
    setBusy(false)
    if (err) { setError("That code didn't work. Check it against the email, or send a new code."); return }
    recordLogin('code')
  }

  async function sendNewCode() {
    if (busy) return
    setError('')
    setBusy(true)
    try {
      await requestCode(cleanEmail(), afterPayment, door.key)
      setResent(true)
      setCode('')
    } catch {
      setError("That didn't go through. Check your connection and try again.")
    } finally {
      setBusy(false)
    }
  }

  function differentEmail() {
    setStep('email')
    setResent(false)
    setCode('')
    setError('')
  }

  function toggleMode() {
    setMode(m => (m === 'code' ? 'password' : 'code'))
    setError('')
  }

  return (
    <DoorFrame door={door}>
      {step === 'email' && (
        <form className="door-form" onSubmit={mode === 'code' ? sendCode : logInWithPassword} noValidate>
          <h1 className="door-title">{title}</h1>
          <p className="door-lede">{showLinkFailed ? LINK_FAILED.lede : `We'll email you an ${SITE.codeLength}-digit code. No password needed.`}</p>
          <div className="door-field">
            <label htmlFor="email">Email address</label>
            <input id="email" type="email" autoComplete="email" inputMode="email" autoCapitalize="none" spellCheck="false"
              value={email} onChange={e => setEmail(e.target.value)} />
          </div>
          {mode === 'password' && (
            <>
              <div className="door-field">
                <label htmlFor="pw">Password</label>
                <input id="pw" type="password" autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} />
              </div>
              <button type="submit" className="door-btn" disabled={busy}>Log in</button>
            </>
          )}
          {mode === 'code' && <button type="submit" className="door-btn" disabled={busy}>{showLinkFailed ? LINK_FAILED.button : 'Email me a code'}</button>}
          {error && <p className="door-error" role="alert">{error}</p>}
          <div className="door-links">
            <button type="button" className="door-link" onClick={toggleMode}>
              {mode === 'code' ? 'Use a password instead' : 'Email me a code instead'}
            </button>
            <button type="button" className="door-link" aria-expanded={help} onClick={() => setHelp(h => !h)}>
              Paid with a different email?
            </button>
          </div>
          {help && (
            <div className="door-help">
              Log in with the email you paid with. If you can't get into that inbox, email {SITE.helpEmail} and we'll move your access to this one.
            </div>
          )}
        </form>
      )}

      {step === 'code' && (
        <form className="door-form" onSubmit={logInWithCode} noValidate>
          <h1 className="door-title">Check your email</h1>
          <p className="door-lede">We've sent an {SITE.codeLength}-digit code to {cleanEmail()}. It works for the next hour.</p>
          <div className="door-field">
            <label htmlFor="code">Your code</label>
            <input id="code" ref={codeRef} className="door-code" inputMode="numeric" autoComplete="one-time-code"
              value={code} onChange={e => setCode(e.target.value.replace(/[^0-9]/g, '').slice(0, SITE.codeLength))} />
          </div>
          <button type="submit" className="door-btn" disabled={busy}>Log in</button>
          {error && <p className="door-error" role="alert">{error}</p>}
          <div className="door-links">
            <button type="button" className="door-link" onClick={differentEmail}>Use a different email</button>
            <button type="button" className="door-link" onClick={sendNewCode} disabled={busy}>
              {resent ? 'New code sent' : 'Send a new code'}
            </button>
          </div>
          {/* the same for every email, so it never says who's a customer (member review, 11 Oct) */}
          <p className="door-note">Nothing after a few minutes? Check your spam folder, or try the email you paid with. Still stuck? Email <a href={`mailto:${SITE.helpEmail}`}>{SITE.helpEmail}</a>.</p>
        </form>
      )}
    </DoorFrame>
  )
}

// A login link that no longer works (used, run out, or cut short), shown on its door's email step
// until a new code has been sent. No board draws it; the words are in the board's tone.
export const LINK_FAILED = {
  title: 'This link has expired',
  lede: "Login links work once, for an hour after we send them. Type your email address and we'll send you a new code.",
  button: 'Email me a new code',
}

// A door: its own look on the left, and on the right whatever the page puts there, with Property
// Cashflow For Life's name over it and the Terms and Privacy links under it. The login and the login
// link's page (screens/SignIn.jsx) share it.
export function DoorFrame({ door, children }) {
  return (
    <div className={`door door--${door.key}`}>
      <div className="door-left"><DoorArt door={door} /></div>
      <div className="door-right">
        {door.key === 'cashflow' && (
          <div className="door-brand">
            <Pentagon size={40} stroke={1.4} lit={2.4} />
            <div className="door-brand-text">
              <span className="door-brand-name">{door.name}</span>
              <span className="door-brand-line">{door.tagline}</span>
            </div>
          </div>
        )}

        {children}

        <div className="door-legal">
          <a href={SITE.termsUrl}>Terms</a>
          <a href={SITE.privacyUrl}>Privacy</a>
        </div>
      </div>
    </div>
  )
}

// The left half of each door, as the board draws it
function DoorArt({ door }) {
  switch (door.key) {
    case 'cashflow':
      return <img className="door-photo" src={housePhoto} alt="A house on the course's patch" />
    case 'poyt':
      return (
        <div className="door-art door-art--centre">
          <div className="door-eyebrow">{door.eyebrow}</div>
          <div className="door-poyt-name">Profit On<br /><span className="poyt-underline">Your Terms<span aria-hidden="true" /></span></div>
          <div className="door-tagline">{door.tagline}</div>
        </div>
      )
    case 'masterclass':
      return (
        <div className="door-art door-art--centre">
          <div className="door-eyebrow">{door.eyebrow}</div>
          <div className="door-poyt-name door-masterclass-name">{door.name}</div>
        </div>
      )
    case 'fe':
      return (
        <div className="door-art door-art--centre door-art--fe">
          <div className="door-fe-bar" aria-hidden="true" />
          <div className="door-fe-name">THE<br />FUNDING<br />ENGINE</div>
        </div>
      )
    case 'acc':
      return (
        <div className="door-art door-art--spread">
          <div className="door-acc-name">{door.name}</div>
          <div className="door-acc-foot">
            <div className="door-acc-rule" aria-hidden="true" />
            <div className="door-acc-line">{door.line}</div>
          </div>
        </div>
      )
    default:
      return (
        <div className="door-art door-art--spread">
          <div className="mark mark--door">
            <span className="mark-name">Adam Slee</span>
            <span className="mark-dot" aria-hidden="true" />
            <span className="mark-sub">Members</span>
          </div>
          <div className="door-lib-middle">
            <div className="door-lib-rule" aria-hidden="true" />
            <div className="door-lib-head">Everything you've joined, with 1 login.</div>
            <div className="door-swatches">
              <span title="The Funding Engine" className="swatch swatch--fe" />
              <span title="The Property Accelerator" className="swatch swatch--acc" />
              <span title="Profit On Your Terms" className="swatch swatch--poyt" />
              <span title="Property Cashflow For Life" className="swatch swatch--pcfl" />
              <span title="Buy your next property without a deposit" className="swatch swatch--masterclass" />
            </div>
          </div>
          <div className="door-lib-company">{SITE.company}</div>
        </div>
      )
  }
}
